import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useState } from "react";
import { logger } from "@/utils/logger";
import { PLAN_LIMITS, type UserPlan } from "@/lib/planConstants";

interface PlanLimits {
  pages: number;
  submissions: number;
  period: 'month';
  teamMembers: number;
  campaigns: number;
}

interface UsageData {
  total_pages: number;
  total_submissions_today: number;
  total_submissions_month: number;
}

export const usePlanLimits = () => {
  const { user } = useAuth();
  const [plan, setPlan] = useState<UserPlan>('starter');
  const [usage, setUsage] = useState<UsageData | null>(null);
  const [loading, setLoading] = useState(true);

  const planLimits: Record<UserPlan, PlanLimits> = {
    starter: { ...PLAN_LIMITS.starter, period: 'month' as const },
    professional: { ...PLAN_LIMITS.professional, period: 'month' as const },
    business: { ...PLAN_LIMITS.business, period: 'month' as const }
  };

  const getCampaignLimit = () => {
    if (plan === 'starter') return 1;
    if (plan === 'professional') return 3;
    return Infinity;
  };

  const fetchPlanAndUsage = async () => {
    if (!user) return;

    try {
      const { data: profile } = await supabase
        .from('profiles')
        .select('plan')
        .eq('id', user.id)
        .single();

      if (profile) {
        setPlan(profile.plan as UserPlan);
      }

      const { data: usageData } = await supabase
        .rpc('get_user_usage', { user_uuid: user.id })
        .single();

      if (usageData) {
        setUsage(usageData);
      }
    } catch (error) {
      logger.error('Error fetching plan and usage', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlanAndUsage();
  }, [user?.id]);

  const canCreatePage = () => {
    if (!usage) return false;
    const limit = planLimits[plan].pages;
    return limit === Infinity || usage.total_pages < limit;
  };

  const canAcceptSubmission = () => {
    if (!usage) return false;
    const limit = planLimits[plan].submissions;
    if (limit === Infinity) return true;
    
    const currentUsage = usage.total_submissions_month;
    
    return currentUsage < limit;
  };

  const getLimitMessage = (type: 'page' | 'submission') => {
    const limits = planLimits[plan];
    
    if (type === 'page') {
      return `You've reached your plan limit of ${limits.pages} feedback ${limits.pages === 1 ? 'page' : 'pages'}. Upgrade to create more.`;
    }
    
    return `You've reached your plan limit of ${limits.submissions} submissions per ${limits.period}. Upgrade for more capacity.`;
  };

  return {
    plan,
    usage,
    loading,
    canCreatePage,
    canAcceptSubmission,
    getLimitMessage,
    limits: planLimits[plan],
    campaignLimit: getCampaignLimit(),
    hasExecutiveReports: plan === 'business',
    refetch: fetchPlanAndUsage
  };
};
